/**
 * WHAT THE BELL SAYS — the call-outs a person collects, put into words.
 *
 * A notification is a row the database wrote FOR one person when something
 * happened that concerns them: someone named them in a comment, replied on a
 * thread they are in, asked them to sign something, or decided on something they
 * sent out (see migrations 0104 and 0105). This turns those rows into the lines
 * the panel shows and settles what is new and what is already seen.
 *
 * EVERY WORD HERE IS FOR THE PERSON READING IT. No table names, no kinds spelled
 * the way the database spells them, no mention of how any of it is stored — a
 * line tells someone what happened and where, and nothing else.
 */

// The worded short month from the single source (R16 — "Sept", never "Sep"), so a
// notification date never drifts to the browser locale's own short form ("Sep").
import { MONTHS } from './when';

export type NotificationKind =
  | 'mention'
  | 'reply'
  | 'signoff_requested'
  | 'signoff_decided'
  | 'cover_delegated'
  | 'cover_arranged'
  | 'status_posted'
  | 'blocked_on_you'
  | 'moved_with'
  | 'card_idle'
  // Many cards changed at once, folded to one call-out per person (migration
  // 0209): someone assigned you a batch of cards, or moved a batch of yours.
  | 'assigned_bulk'
  | 'moved_bulk';

/** One call-out, as the app holds it (the database's row, in the app's words). */
export interface Notification {
  id: string;
  /** Whose call-out this is — the recipient's address, lowercased. */
  owner: string;
  kind: NotificationKind;
  /** Who did the thing: the person who wrote, asked, or decided. */
  actor: string;
  boardId: string | null;
  boardName: string;
  /** What it points at: 'issue' | 'document' | 'release' | 'sprint'. */
  targetKind: string;
  targetId: string | null;
  targetRef: string;
  targetTitle: string;
  /** The comment's text, or the answer word on a decision. */
  snippet: string;
  createdAt: number;
  /** Null until the person has seen it. */
  readAt: number | null;
  /**
   * Null until the person HANDLED it — replied to it, or marked it done in My Work
   * (migration 0144). Separate from readAt because reading is not handling: the
   * inbox shows what is not yet handled, and a handled call-out leaves the inbox
   * while staying in the centre and the trail.
   */
  handledAt: number | null;
}

export const isUnread = (n: Notification) => n.readAt == null;
/** Dealt with — replied to, or marked done. The My Work inbox clears these. */
export const isHandled = (n: Notification) => n.handledAt != null;

export const unreadCount = (list: readonly Notification[]) => list.filter(isUnread).length;

/**
 * THE KINDS THAT WANT A RESPONSE FROM YOU — in one place, #457.
 *
 * My Work's Inbox splits its rows into "Needs my action" and "FYI", and the
 * "My work" nav row carries a badge of the ones that need action. Both read
 * this same set, so the tab heading, its count, and the nav badge can never
 * disagree about what counts as "needs me". A sign-off asked of me, a mention
 * that pulls me in, and a cover handed to me are things I must act on; a reply,
 * a decision, an arranged cover and a posted status are news.
 *
 * WHAT MAKES ONE STILL OUTSTANDING IS THAT IT IS UNHANDLED, NOT UNREAD. Reading
 * a sign-off request does not sign it; the obligation stands until I reply or
 * mark it done (handledAt). An earlier version counted only the UNREAD ones, so
 * two read-but-unactioned call-outs sat in "Needs my action" while the masthead
 * said "all caught up" — not true. The count is now the unhandled ones.
 */
export const NEEDS_ACTION: ReadonlySet<NotificationKind> = new Set<NotificationKind>([
  'signoff_requested',
  'mention',
  'cover_delegated',
  'blocked_on_you',
  // A card of yours has gone quiet — it wants you to move it on or put it on hold,
  // so it sits in "Needs you first", not FYI (project-idle-nudge).
  'card_idle',
]);

/** Does this call-out want an action from me (versus being news to read)? */
export const needsAction = (n: Notification) => NEEDS_ACTION.has(n.kind);

/**
 * The sign-off / cover family — call-outs whose pending work lives on the
 * Sign-offs screen (the 'approvals' view), not on a card and not in Documents.
 * Opening one routed to the generic Documents list, which is empty of sign-offs,
 * so "Open"/"Sign" dead-ended; these lead to where the signature actually is.
 */
export const SIGNOFF_KINDS: ReadonlySet<NotificationKind> = new Set<NotificationKind>([
  'signoff_requested',
  'signoff_decided',
  'cover_delegated',
  'cover_arranged',
]);

/** Does opening this call-out belong on the Sign-offs screen? */
export const leadsToSignoffs = (n: Notification) => SIGNOFF_KINDS.has(n.kind);

/** How many call-outs are still waiting on an action from me — the nav badge.
 *  Unhandled, not unread: reading one does not discharge it (#2). */
export const inboxActionCount = (list: readonly Notification[]) =>
  list.filter((n) => needsAction(n) && !isHandled(n)).length;

/** Newest first — the order the panel reads in. */
export const newestFirst = (list: readonly Notification[]) =>
  [...list].sort((a, b) => b.createdAt - a.createdAt);

/**
 * The two headings the panel shows: what has not been seen, and what has. The
 * split is by whether it has been read, not by age — "New" is the thing you have
 * not looked at, which is what the bell is counting.
 */
export function grouped(list: readonly Notification[]): {
  unread: Notification[];
  read: Notification[];
} {
  const sorted = newestFirst(list);
  return {
    unread: sorted.filter(isUnread),
    read: sorted.filter((n) => !isUnread(n)),
  };
}

/** A word in a line, bold or not, so the panel can weight names and references. */
export interface Segment {
  t: string;
  b?: boolean;
}

/** The icon a row wears, from the app's own palette. */
export function icon(n: Notification): { cls: string; glyph: string } {
  switch (n.kind) {
    case 'mention':
      return { cls: 'at', glyph: '@' };
    case 'reply':
      return { cls: 'rep', glyph: '↩' }; // ↩
    case 'signoff_requested':
      return { cls: 'sign', glyph: '✎' }; // ✎
    case 'signoff_decided':
      return { cls: 'ok', glyph: '✓' }; // ✓
    case 'cover_delegated':
    case 'cover_arranged':
      return { cls: 'sign', glyph: '✎' }; // same sign-off family
    case 'status_posted':
      return { cls: 'upd', glyph: '◉' }; // a progress note — echoes the log's timeline ring
    case 'blocked_on_you':
      return { cls: 'blk', glyph: '◴' }; // a clock — a card is held up waiting on you
    case 'moved_with':
      return { cls: 'upd', glyph: '→' }; // a shift — this card's dates moved with another
    case 'card_idle':
      return { cls: 'idle', glyph: '◷' }; // a quiet clock, neutral — a gentle prompt, not an alarm
    case 'assigned_bulk':
      return { cls: 'upd', glyph: '⇢' }; // a batch handed to you — a fuller arrow than a single move
    case 'moved_bulk':
      return { cls: 'upd', glyph: '→' }; // a batch of your cards shifted — same shift glyph as moved_with
  }
}

/** Did a decision come back a yes? Anything that reads like a refusal is a no. */
export const wasApproved = (answer: string) => !/reject|declin|no\b|not\b|chang/i.test(answer.trim());

/**
 * The headline of a row — "who did what, and where" — with the name and the
 * reference weighted. `nameOf` turns an address into the name the reader knows.
 */
export function line(n: Notification, nameOf: (email: string) => string): Segment[] {
  const who = nameOf(n.actor) || 'Someone';
  const ref = n.targetRef;
  const title = n.targetTitle;
  switch (n.kind) {
    case 'mention':
      return ref
        ? [{ t: who, b: true }, { t: ' mentioned you on ' }, { t: ref, b: true }]
        : [{ t: who, b: true }, { t: ' mentioned you' }];
    case 'reply':
      return ref
        ? [{ t: who, b: true }, { t: ' replied on a thread you’re in — ' }, { t: ref, b: true }]
        : [{ t: who, b: true }, { t: ' replied on a thread you’re in' }];
    case 'signoff_requested':
      return [{ t: title || 'A document', b: true }, { t: ' is waiting on your signature' }];
    case 'signoff_decided': {
      const verb = wasApproved(n.snippet) ? ' signed off ' : ' sent back ';
      return [{ t: who, b: true }, { t: verb }, { t: title || 'what you sent', b: true }];
    }
    case 'cover_delegated':
      // The reader is the nominee; the title carries the person they now cover.
      return [
        { t: 'You can sign for ' },
        { t: nameOf(title) || 'a colleague', b: true },
        { t: ' while they’re away' },
      ];
    case 'cover_arranged':
      // The reader is the covered person; the title carries their nominee.
      return [
        { t: nameOf(title) || 'A colleague', b: true },
        { t: ' will sign your sign-offs while you’re away' },
      ];
    case 'status_posted':
      return ref
        ? [{ t: who, b: true }, { t: ' posted an update on ' }, { t: ref, b: true }]
        : [{ t: who, b: true }, { t: ' posted a status update' }];
    case 'blocked_on_you':
      return ref
        ? [{ t: who, b: true }, { t: ' is waiting on you to unblock ' }, { t: ref, b: true }]
        : [{ t: who, b: true }, { t: ' is waiting on you' }];
    case 'moved_with':
      // The reader is the dependent's doer; the dates moved because a card it waits
      // on finished later. The snippet carries the new due date.
      return ref
        ? [{ t: ref, b: true }, { t: '’s dates moved — it waits on work that was pushed later' }]
        : [{ t: 'A card’s dates moved — it waits on work that was pushed later' }];
    case 'card_idle':
      // The reader is the card's doer; it has gone quiet. The snippet carries how
      // long and what to do (set by the raiser). Plain, gentle — not a telling-off.
      return ref
        ? [{ t: 'Nothing has moved on ' }, { t: ref, b: true }]
        : [{ t: 'A card of yours has gone quiet' }];
    case 'assigned_bulk': {
      // One line for a whole batch; the count rides in targetRef (migration 0209).
      const n1 = Number(ref) === 1;
      return [
        { t: who, b: true },
        { t: ' assigned you ' },
        { t: ref || '0', b: true },
        { t: n1 ? ' card' : ' cards' },
      ];
    }
    case 'moved_bulk':
      // "moved N of your cards" — the partitive stays plural even for one.
      return [
        { t: who, b: true },
        { t: ' moved ' },
        { t: ref || '0', b: true },
        { t: ' of your cards' },
      ];
  }
}

/** The quieter second line, when there is one to show. */
export function snippet(n: Notification, nameOf: (email: string) => string): string {
  switch (n.kind) {
    case 'mention':
    case 'reply':
      return n.snippet ? `“${n.snippet}”` : '';
    case 'signoff_requested':
      return n.actor ? `Sent by ${nameOf(n.actor) || 'a colleague'}.` : '';
    case 'signoff_decided':
      return wasApproved(n.snippet)
        ? 'It is now signed off.'
        : 'They have asked for changes.';
    case 'cover_delegated':
      // A reason only exists when an admin arranged it; otherwise nothing to add.
      return n.snippet ? `“${n.snippet}”` : '';
    case 'cover_arranged':
      return n.snippet
        ? `“${n.snippet}”`
        : n.actor
          ? `Arranged by ${nameOf(n.actor) || 'an admin'}.`
          : '';
    case 'status_posted':
      return n.snippet ? `“${n.snippet}”` : '';
    case 'blocked_on_you':
      return n.snippet ? `“${n.snippet}”` : '';
    case 'moved_with':
      // A system line, not a human quote — shown plain, no quotation marks.
      return n.snippet;
    case 'card_idle':
      // Plain, no quotes — "No change for 5 working days. Post an update or put it
      // on hold." (the raiser fills in the span).
      return n.snippet;
    case 'assigned_bulk':
    case 'moved_bulk':
      // The titles of the cards in the batch, comma-joined by the database — a
      // list, not a human quote, so it is shown plain, no quotation marks.
      return n.snippet;
  }
}

/**
 * When it happened, said the way a person reads a clock: the time today,
 * "Yesterday" and the time, otherwise the date. `now` is passed so it can be
 * tested and so every row on one open agrees on where "today" ends.
 */
export function whenLabel(ms: number, now: number = Date.now()): string {
  const at = new Date(ms);
  const time = at
    .toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })
    .replace(/\s/g, ' ');
  const startOfToday = new Date(now);
  startOfToday.setHours(0, 0, 0, 0);
  const dayMs = 86_400_000;
  if (ms >= startOfToday.getTime()) return `Today, ${time}`;
  if (ms >= startOfToday.getTime() - dayMs) return `Yesterday, ${time}`;
  const sameYear = at.getFullYear() === new Date(now).getFullYear();
  const date = `${at.getDate()} ${MONTHS[at.getMonth()]}${sameYear ? '' : ` ${at.getFullYear()}`}`;
  return `${date}, ${time}`;
}

/** The board or project a row happened on, for the meta line under it. */
export const place = (n: Notification) => n.boardName;

/** The meta line: where it happened, then when. */
export function meta(n: Notification, now: number = Date.now()): string {
  const where =
    n.kind === 'mention' || n.kind === 'reply' || n.kind === 'status_posted' || n.kind === 'blocked_on_you' || n.kind === 'moved_with' || n.kind === 'card_idle'
      ? n.targetTitle || n.boardName
      : n.boardName;
  return [where, whenLabel(n.createdAt, now)].filter(Boolean).join(' · ');
}
